import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Frame, X as CloseIcon, ChevronDown } from 'lucide-react';
import CustomCursor from '@/components/CustomCursor';
import CodeRain from '@/components/CodeRain';
import Header from '@/components/Header';
import ArtworkCard from '@/components/ArtworkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FilterChip({ label, value, onClear, testId }) {
  return (
    <div
      data-testid={testId}
      className="inline-flex items-center gap-2 border border-[#90E0EF]/50 bg-[#90E0EF]/10 text-[#90E0EF] px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em]"
    >
      <span className="text-[#7ab7c8]">{label}:</span>
      <span>{value}</span>
      <button
        onClick={onClear}
        aria-label={`Clear ${label} filter`}
        className="ml-1 hover:text-white transition-colors"
      >
        <CloseIcon className="w-3 h-3" />
      </button>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testId, disabled }) {
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-[9px] font-mono uppercase tracking-[0.28em] text-[#555] mb-1.5">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none bg-transparent border border-[#1f1f1f] px-3 py-2.5 pr-9 text-[12px] font-mono text-[#f6f6f6] focus:outline-none focus:border-[#90E0EF]/40 disabled:opacity-30 transition-colors"
        >
          <option value="">All</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" />
      </div>
    </div>
  );
}

export default function PublicArtworksPage() {
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const [artistFilter, setArtistFilter] = useState('');
  const [exhibitionFilter, setExhibitionFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // artists picklist for the filter
    axios.get(`${API}/public/artists`).then((r) => setArtists(r.data.artists || [])).catch(() => {});
    // exhibitions optional — silently degrade if endpoint 404s
    axios.get(`${API}/public/exhibitions`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.exhibitions || []);
      setExhibitions(list);
    }).catch(() => setExhibitions([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (artistFilter) params.set('artist_id', artistFilter);
    if (exhibitionFilter) params.set('exhibition_id', exhibitionFilter);
    axios
      .get(`${API}/public/artworks${params.toString() ? `?${params}` : ''}`)
      .then((r) => setArtworks(r.data.artworks || []))
      .finally(() => setLoading(false));
  }, [artistFilter, exhibitionFilter]);

  const artistOpts = useMemo(
    () => artists.map((a) => ({ value: a.id, label: a.name })),
    [artists],
  );
  const exhibitionOpts = useMemo(
    () => (exhibitions || []).map((e) => ({ value: e.id, label: e.title || e.name || 'Untitled' })),
    [exhibitions],
  );

  const artistLabel = artistFilter ? (artistOpts.find((a) => a.value === artistFilter)?.label || artistFilter) : null;
  const exhibitionLabel = exhibitionFilter ? (exhibitionOpts.find((e) => e.value === exhibitionFilter)?.label || exhibitionFilter) : null;

  return (
    <div className="bg-[#050505] min-h-screen text-[#F5F5F5]">
      <CustomCursor />
      <CodeRain />
      <Header />

      <main className="relative z-10" style={{ paddingTop: 'calc(var(--header-height) + 32px)' }}>
        {/* Hero */}
        <section className="px-6 md:px-10 lg:px-16 pb-6 md:pb-10 border-b border-[#111]">
          <div className="max-w-7xl mx-auto">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">
              THE RELATIVE GALLERY
            </p>
            <h1 className="mt-3 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-outfit font-bold text-white tracking-tighter leading-[0.95] uppercase">
              Artworks
            </h1>
            <p className="mt-5 max-w-xl text-[13px] font-mono text-[#888] leading-relaxed">
              Prints and editions from artists exhibiting at The Relative Gallery.
            </p>
          </div>
        </section>

        {/* Filters + grid */}
        <section className="px-6 md:px-10 lg:px-16 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            {/* Filter row */}
            <div className="grid grid-cols-1 md:grid-cols-[240px_240px_1fr] items-end gap-6 mb-8">
              <FilterSelect
                label="Filter by artist"
                value={artistFilter}
                options={artistOpts}
                onChange={setArtistFilter}
                testId="filter-artist"
              />
              <FilterSelect
                label="Filter by exhibition"
                value={exhibitionFilter}
                options={exhibitionOpts}
                onChange={setExhibitionFilter}
                testId="filter-exhibition"
                disabled={exhibitionOpts.length === 0}
              />
              <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end pb-1">
                {artistLabel && (
                  <FilterChip
                    testId="filter-chip-artist"
                    label="Artist"
                    value={artistLabel}
                    onClear={() => setArtistFilter('')}
                  />
                )}
                {exhibitionLabel && (
                  <FilterChip
                    testId="filter-chip-exhibition"
                    label="Exhibition"
                    value={exhibitionLabel}
                    onClear={() => setExhibitionFilter('')}
                  />
                )}
                {!loading && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#555]" data-testid="artworks-count">
                    {artworks.length} {artworks.length === 1 ? 'work' : 'works'}
                  </span>
                )}
              </div>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
              </div>
            ) : artworks.length === 0 ? (
              <div className="text-center py-24 border border-[#111]">
                <Frame className="w-10 h-10 text-[#222] mx-auto" />
                <p className="text-[13px] font-mono text-[#666] mt-4">
                  No artworks match this filter.
                </p>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={`${artistFilter}-${exhibitionFilter}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
                  data-testid="public-artworks-grid"
                >
                  {artworks.map((a) => (
                    <ArtworkCard key={a.id} artwork={a} />
                  ))}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
